import { useQuery } from '@apollo/client/react';
import { UserAvatar } from '@medcom/shared-ui/components/UserAvatar.tsx';
import type { User } from 'oidc-client-ts';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { signOut } from '../../ais/aisSignin.ts';
import { aisIdentityOf } from '../../ais/useAisIdentity.ts';
import { UserMenu } from '../../components/UserMenu';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { oidcConfig } from '../../oidcConfig.ts';
import { useSocket } from '../../socket/useSocket.ts';
import { hasAnyRelevantRole, hasSomeRole, Role, realmRoles, translateRole, userFullname } from '../../utils/user';
import { AssignCurrentDoctorModal } from './AssignCurrentDoctorModal';
import { USER_MENU_QUERY } from './UserMenuWrapper.gql';

const openAccountConsole = () => {
  window.location.href =
    oidcConfig.authority + `/account?referrer=${oidcConfig.client_id}&referrer_uri=${encodeURIComponent(window.location.href)}`;
};

// AIS accounts hold no account client role, the console would only answer forbidden
const accountConsoleFor = (user: User | null | undefined) => (aisIdentityOf(user) ? undefined : openAccountConsole);

export const UserMenuWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { socket } = useSocket();

  const [assignDoctorOpen, setAssignDoctorOpen] = useState(false);

  const auth = useAuth();
  const { currentDoctorId, setCurrentDoctorId } = useCurrentContextStore();

  const roleNames =
    realmRoles(auth.user)
      .filter(it => Object.keys(Role).includes(it))
      .map(it => translateRole(it))
      .join(', ') ?? '';
  const hasAnyRole = hasAnyRelevantRole(auth.user);

  useEffect(() => {
    socket.on('sso-logout', event => {
      if (auth.user?.session_state === event.sessionId) {
        signOut(auth); // redirects to kc logout page
      }
    });
    return () => {
      socket.off('sso-logout');
    };
  }, [socket, auth]);

  const updateCurrentDoctorId = (doctorId: string | undefined | null) => {
    if (!doctorId) {
      return;
    }

    setCurrentDoctorId(doctorId);
    setAssignDoctorOpen(false);
    navigate('/');
  };

  const { data, loading } = useQuery(USER_MENU_QUERY, {
    variables: {
      doctorId: currentDoctorId,
    },
    fetchPolicy: 'cache-and-network', // cache here to have the header info updated automatically when editing the current doctor
    skip: !currentDoctorId || !hasAnyRole,
  });

  const userFullName = userFullname(auth.user, auth.user?.profile.preferred_username);
  const doctorName = data?.doctor ? data.doctor.name : loading ? '' : 'Kein Zuweiser festgelegt';

  useEffect(() => {
    if (data?.doctor && currentDoctorId && doctorName && auth.user?.profile.sub && userFullName) {
      window.nativeApi?.setUserInfo({
        doctorId: currentDoctorId,
        doctorName: doctorName,
        userId: auth.user.profile.sub,
        userName: userFullName,
      });
    } else {
      window.nativeApi?.setUserInfo(null);
    }
  }, [data, currentDoctorId, doctorName, auth, userFullName]);

  return (
    <>
      <UserMenu
        roleName={roleNames}
        // an AIS account is named after the vendor's user id, which means nothing to the person
        username={aisIdentityOf(auth.user) ? userFullName : auth.user?.profile.preferred_username}
        disabled={data?.doctor?.disabled ?? false}
        avatar={
          <UserAvatar
            userId={auth.user?.profile.sub}
            givenName={auth.user?.profile.given_name}
            familyName={auth.user?.profile.family_name}
            username={auth.user?.profile.preferred_username}
          />
        }
        doctorName={doctorName}
        userFullName={userFullName}
        showDoctorSelect={
          hasAnyRole &&
          ((data?.assignmentForCurrentUser?.additionalDoctors?.length ?? 0) > 0 ||
            hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_LAB_STAFF, Role.ROLE_LR_FRONT_DESK], auth.user))
        }
        showDoctorReset={hasAnyRole && !!currentDoctorId && data?.assignmentForCurrentUser?.primaryDoctor.id !== currentDoctorId}
        onDoctorSelect={() => setAssignDoctorOpen(true)}
        onDoctorReset={() => updateCurrentDoctorId(data?.assignmentForCurrentUser?.primaryDoctor.id)}
        onAccountOpen={accountConsoleFor(auth.user)}
        onLogout={() => {
          const sessionId = auth.user?.session_state;
          if (sessionId) {
            socket.emit('sso-logout', sessionId);
          }
        }}
      />
      <AssignCurrentDoctorModal
        currentDoctorId={currentDoctorId}
        open={assignDoctorOpen}
        onSuccess={updateCurrentDoctorId}
        onClose={() => setAssignDoctorOpen(false)}
      />
    </>
  );
};
